/**
 * TierBadge — coloured badge showing a user's tier.
 *
 * The name and caption come from Admin → Tier Names via useTierNames(); this
 * component never contains a tier name of its own.
 *
 * Usage: <TierBadge tier="tier1" /> or <TierBadge tier="free" size="small" />
 */
import { useTierNames } from '@/lib/tierNames';

interface TierBadgeProps {
  tier: string;
  /** Gallery and Explorer accounts read the same tier value under their own name. */
  profileType?: string;
  size?: 'small' | 'normal';
}

export default function TierBadge({ tier, profileType, size = 'normal' }: TierBadgeProps) {
  const { tierName, tierLabel, tierColor } = useTierNames();
  const name = tierName(tier, profileType);
  const label = tierLabel(tier, profileType);
  const color = tierColor(tier, profileType);
  const isSmall = size === 'small';

  if (!name) return null;

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: isSmall ? 3 : 5,
      fontSize: isSmall ? '0.65rem' : '0.75rem',
      padding: isSmall ? '0.15rem 0.5rem' : '0.25rem 0.75rem',
      borderRadius: 20,
      fontWeight: 600,
      background: `${color}20`,
      color,
      border: `1px solid ${color}40`,
      whiteSpace: 'nowrap',
    }}>
      {name}{label ? <span style={{ opacity: 0.7 }}>({label})</span> : null}
    </span>
  );
}
